import React, { useState } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonDatetime,
  IonToggle,
  IonModal,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  useIonToast,
} from '@ionic/react';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateOrderDto, IMenuItem, PaymentMethod } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency } from '../utils/format';

interface OrderItemRow {
  menuItemId?: string;
  quantity?: number;
}

interface FormValues {
  customerName: string;
  isNewCustomer: boolean;
  isPaid: boolean;
  paymentMethod: PaymentMethod;
  orderDate?: string;
  items: OrderItemRow[];
}

const OrderForm: React.FC = () => {
  const { t } = useLanguage();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [presentToast] = useIonToast();

  const menuItemsQuery = useQuery({
    queryKey: ['menu-items', 'active'],
    queryFn: async () => {
      const { data } = await apiClient.get<IMenuItem[]>('/menu/active');
      return data;
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      customerName: '',
      isNewCustomer: false,
      isPaid: true,
      paymentMethod: PaymentMethod.CASH,
      orderDate: new Date().toISOString(),
      items: [{ menuItemId: undefined, quantity: 1 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const itemsWatch = useWatch({ control, name: 'items' });

  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  const isPaid = useWatch({
    control,
    name: 'isPaid',
  });

  const menuItems = menuItemsQuery.data ?? [];

  const openPickerForIndex = (index: number) => {
    setPickerIndex(index);
    setPickerSearch('');
  };

  const closePicker = () => setPickerIndex(null);

  const createOrderMutation = useMutation({
    mutationFn: async (payload: CreateOrderDto) => {
      await apiClient.post('/orders', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'summary'] });
      presentToast({
        message: t('orderCreatedSuccessfully') ?? 'Order created successfully',
        duration: 2000,
        color: 'success',
      });
      history.replace('/tabs/orders');
    },
    onError: () => {
      presentToast({
        message: t('orderCreateFailed') ?? 'Failed to create order',
        duration: 2000,
        color: 'danger',
      });
    },
  });

  const onSubmit = async (values: FormValues) => {
    const validItems = values.items.filter(
      (item) => item.menuItemId && Number(item.quantity || 0) > 0,
    );

    if (!validItems.length) {
      presentToast({
        message: t('orderRequiresItems') ?? 'Please add at least one item',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    const payloadItems = validItems.flatMap((item) => {
      if (!item.menuItemId) {
        return [];
      }
      return [
        {
          menuItemId: item.menuItemId,
          quantity: Number(item.quantity || 0),
        },
      ];
    });

    const payload: CreateOrderDto = {
      customerName: values.customerName.trim() || t('walkInCustomer') || 'Walk-in customer',
      isNewCustomer: values.isNewCustomer || undefined,
      isPaid: values.isPaid,
      paymentMethod: values.isPaid ? values.paymentMethod : undefined,
      orderDate: values.orderDate ? new Date(values.orderDate).toISOString() : undefined,
      items: payloadItems,
    };

    await createOrderMutation.mutateAsync(payload);
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/orders" />
          </IonButtons>
          <IonTitle>{t('addOrder') ?? 'Add order'}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {menuItemsQuery.isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('customerName') ?? 'Customer name'}</IonLabel>
                <Controller
                  control={control}
                  name="customerName"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <IonInput
                      value={field.value}
                      onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                    />
                  )}
                />
                {errors.customerName ? (
                  <IonText color="danger">
                    <p>{t('enterName') ?? 'Enter name'}</p>
                  </IonText>
                ) : null}
              </IonItem>

              <IonItem>
                <IonLabel>{t('newCustomer') ?? 'New customer'}</IonLabel>
                <Controller
                  control={control}
                  name="isNewCustomer"
                  render={({ field }) => (
                    <IonToggle
                      checked={field.value}
                      onIonChange={(event) => field.onChange(event.detail.checked)}
                    />
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel>{t('paid')}</IonLabel>
                <Controller
                  control={control}
                  name="isPaid"
                  render={({ field }) => (
                    <IonToggle
                      checked={field.value}
                      onIonChange={(event) => {
                        const checked = event.detail.checked;
                        field.onChange(checked);
                        if (!checked) {
                          setValue('paymentMethod', PaymentMethod.CASH);
                        }
                      }}
                    />
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('paymentMethod') ?? 'Payment method'}</IonLabel>
                <Controller
                  control={control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <IonSelect
                      interface="action-sheet"
                      disabled={!isPaid}
                      value={field.value}
                      onIonChange={(event) => field.onChange(event.detail.value as PaymentMethod)}
                    >
                      <IonSelectOption value={PaymentMethod.CASH}>
                        {t('cash') ?? 'Cash'}
                      </IonSelectOption>
                      <IonSelectOption value={PaymentMethod.CARD}>
                        {t('card') ?? 'Card'}
                      </IonSelectOption>
                      <IonSelectOption value={PaymentMethod.TRANSFER}>
                        {t('transfer') ?? 'Transfer'}
                      </IonSelectOption>
                    </IonSelect>
                  )}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">{t('orderDate') ?? 'Order date'}</IonLabel>
                <Controller
                  control={control}
                  name="orderDate"
                  render={({ field }) => (
                    <IonDatetime
                      presentation="date-time"
                      value={field.value}
                      onIonChange={(event) => field.onChange(event.detail.value ?? undefined)}
                    />
                  )}
                />
              </IonItem>
            </IonList>

            <IonText color="primary">
              <h2 style={{ marginTop: 24 }}>{t('itemsSold')}</h2>
            </IonText>

            <IonList>
              {fields.map((fieldItem, index) => {
                const selectedId = itemsWatch?.[index]?.menuItemId;
                const selectedItem = menuItems.find((item) => item.id === selectedId);
                return (
                  <IonItem key={fieldItem.id} lines="full">
                    <IonLabel position="stacked">{t('menu')}</IonLabel>
                    <IonButton
                      expand="block"
                      fill="outline"
                      type="button"
                      style={{ marginTop: 8 }}
                      onClick={() => openPickerForIndex(index)}
                    >
                      {selectedItem?.product?.name || t('selectMenuItem') || 'Select menu item'}
                    </IonButton>

                    <IonLabel position="stacked" style={{ marginTop: 12 }}>
                      {t('quantity')}
                    </IonLabel>
                    <Controller
                      control={control}
                      name={`items.${index}.quantity`}
                      rules={{ required: true, min: 1 }}
                      render={({ field }) => (
                        <IonInput
                          type="number"
                          value={field.value?.toString() ?? ''}
                          onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                        />
                      )}
                    />

                    <IonButtons slot="end" style={{ marginTop: 12 }}>
                      <IonButton
                        color="danger"
                        type="button"
                        disabled={fields.length === 1}
                        onClick={() => remove(index)}
                      >
                        {t('removeRow') ?? 'Remove'}
                      </IonButton>
                    </IonButtons>
                  </IonItem>
                );
              })}
            </IonList>

            <IonButton
              expand="block"
              type="button"
              style={{ marginTop: 12 }}
              onClick={() => append({ menuItemId: undefined, quantity: 1 })}
            >
              {t('addItem') ?? 'Add item'}
            </IonButton>

            <IonButton
              expand="block"
              type="submit"
              style={{ marginTop: 24 }}
              disabled={isSubmitting || createOrderMutation.isPending}
            >
              {isSubmitting || createOrderMutation.isPending ? t('saving') ?? 'Saving…' : t('save')}
            </IonButton>

            <IonModal isOpen={pickerIndex !== null} onDidDismiss={closePicker}>
              <IonHeader>
                <IonToolbar>
                  <IonTitle>{t('selectMenuItem') ?? 'Select menu item'}</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={closePicker}>{t('close')}</IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent className="ion-padding">
                <IonSearchbar
                  value={pickerSearch}
                  onIonChange={(event) => setPickerSearch(event.detail.value ?? '')}
                  placeholder={t('search') ?? 'Search'}
                />
                <IonList>
                  {menuItems
                    .filter((menuItem) =>
                      (menuItem.product?.name || '')
                        .toLowerCase()
                        .includes(pickerSearch.toLowerCase()),
                    )
                    .map((menuItem) => (
                      <IonItem
                        key={menuItem.id}
                        button
                        detail
                        onClick={() => {
                          if (pickerIndex === null) return;
                          setValue(`items.${pickerIndex}.menuItemId`, menuItem.id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          closePicker();
                        }}
                      >
                        <IonLabel>
                          <h3>{menuItem.product?.name || t('unknown')}</h3>
                          <p>{formatCurrency(Number(menuItem.price || 0))}</p>
                        </IonLabel>
                      </IonItem>
                    ))}
                </IonList>
              </IonContent>
            </IonModal>
          </form>
        )}
      </IonContent>
    </IonPage>
  );
};

export default OrderForm;
